import { useCallback, useEffect, useMemo, useRef } from 'react';
import { logger } from '../utils/logger';

// Default audio playback settings - 24kHz mono PCM from Gemini
const DEFAULT_SAMPLE_RATE = 24000;

interface UseAudioPlaybackOptions {
  sampleRate?: number; // Default: 24000 (Gemini output), use 16000 for loopback
  prebufferMs?: number;
  lowBufferThresholdMs?: number;
  fadeMs?: number;
  onPlaybackStateChange?: (state: 'started' | 'ended' | 'cleared') => void;
}

type AudioEffectMode = 'off' | 'clip' | 'wavefold' | 'bitcrush';

export function useAudioPlayback(options: UseAudioPlaybackOptions = {}) {
  const {
    sampleRate = DEFAULT_SAMPLE_RATE,
    prebufferMs = 50,
    lowBufferThresholdMs = 20,
    fadeMs = 15,
    onPlaybackStateChange,
  } = options;
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const workletReadyRef = useRef<boolean>(false);
  const audioUnlockedRef = useRef<boolean>(false);
  const currentVolumeRef = useRef<number>(1.0);
  const playbackStartedRef = useRef<boolean>(false);
  const currentEffectModeRef = useRef<AudioEffectMode>('off');
  const currentEffectIntensityRef = useRef<number>(0);
  const fallbackModeRef = useRef<boolean>(false);
  const fallbackNextStartTimeRef = useRef<number>(0);
  const fallbackEndedTimeoutRef = useRef<number | null>(null);
  const fallbackSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const fallbackWarningLoggedRef = useRef<boolean>(false);
  const sampleRateMismatchWarningLoggedRef = useRef<boolean>(false);
  const playbackStateChangeRef = useRef<typeof onPlaybackStateChange>(onPlaybackStateChange);

  useEffect(() => {
    playbackStateChangeRef.current = onPlaybackStateChange;
  }, [onPlaybackStateChange]);

  const emitPlaybackState = useCallback((state: 'started' | 'ended' | 'cleared') => {
    if (state === 'started') {
      if (playbackStartedRef.current) return;
      playbackStartedRef.current = true;
    } else if (state === 'ended' || state === 'cleared') {
      if (!playbackStartedRef.current && state === 'ended') return;
      playbackStartedRef.current = false;
    }

    playbackStateChangeRef.current?.(state);
  }, []);

  const clearFallbackEndedTimeout = useCallback(() => {
    if (fallbackEndedTimeoutRef.current) {
      window.clearTimeout(fallbackEndedTimeoutRef.current);
      fallbackEndedTimeoutRef.current = null;
    }
  }, []);

  const resetAudioGraph = useCallback(() => {
    workletReadyRef.current = false;
    workletNodeRef.current = null;
    gainNodeRef.current = null;
    fallbackNextStartTimeRef.current = 0;
    fallbackSourcesRef.current.clear();
    audioUnlockedRef.current = false;
  }, []);

  const stopFallbackSources = useCallback(() => {
    for (const source of fallbackSourcesRef.current) {
      try {
        source.stop();
      } catch {
        // Ignore sources that already ended or were never started.
      }

      try {
        source.disconnect();
      } catch {
        // Ignore disconnect races during teardown.
      }
    }

    fallbackSourcesRef.current.clear();
  }, []);

  const ensureGainNode = useCallback((audioContext: AudioContext) => {
    if (audioContext.state === 'closed') {
      throw new Error('Audio context is closed');
    }

    if (gainNodeRef.current && gainNodeRef.current.context === audioContext) {
      return gainNodeRef.current;
    }

    if (gainNodeRef.current) {
      try {
        gainNodeRef.current.disconnect();
      } catch {
        // Ignore stale node cleanup errors
      }
      gainNodeRef.current = null;
    }

    const gainNode = audioContext.createGain();
    gainNode.gain.value = currentVolumeRef.current;
    gainNode.connect(audioContext.destination);
    gainNodeRef.current = gainNode;
    return gainNode;
  }, []);

  const scheduleFallbackEndedState = useCallback((audioContext: AudioContext, endTime: number) => {
    clearFallbackEndedTimeout();
    const delay = Math.max(0, Math.round((endTime - audioContext.currentTime) * 1000) + 80);
    fallbackEndedTimeoutRef.current = window.setTimeout(() => {
      fallbackEndedTimeoutRef.current = null;
      emitPlaybackState('ended');
    }, delay);
  }, [clearFallbackEndedTimeout, emitPlaybackState]);

  const initWorklet = useCallback(async (audioContext: AudioContext) => {
    if (fallbackModeRef.current) {
      ensureGainNode(audioContext);
      return null;
    }

    if (audioContext.sampleRate !== sampleRate) {
      fallbackModeRef.current = true;
      workletReadyRef.current = false;
      workletNodeRef.current = null;
      ensureGainNode(audioContext);
      if (!sampleRateMismatchWarningLoggedRef.current) {
        sampleRateMismatchWarningLoggedRef.current = true;
        console.warn(
          `[AudioPlayback] AudioContext sample rate ${audioContext.sampleRate}Hz does not match source ${sampleRate}Hz; using buffer fallback`,
        );
      }
      return null;
    }

    if (workletReadyRef.current && workletNodeRef.current) {
      return workletNodeRef.current;
    }

    try {
      // Load the AudioWorklet module with cache-busting
      const cacheBuster = `?v=${Date.now()}`;
      await audioContext.audioWorklet.addModule(`/worklets/playback-processor.js${cacheBuster}`);

      // Create the worklet node
      const workletNode = new AudioWorkletNode(audioContext, 'playback-processor', {
        processorOptions: {
          prebufferMs,
          lowBufferThresholdMs,
          fadeMs,
        },
      });

      // Create gain node for volume control
      const gainNode = ensureGainNode(audioContext);

      // Connect: worklet -> gain -> destination
      workletNode.connect(gainNode);

      // Listen for status messages from worklet
      workletNode.port.onmessage = (event) => {
        const data = event.data as {
          type?: string;
          samplesAvailable?: number;
          isBuffering?: boolean;
        };
        if (data?.type !== 'status') {
          return;
        }

        const samplesAvailable = data.samplesAvailable ?? 0;
        const isBuffering = data.isBuffering ?? false;
        const isAudiblyPlaying = samplesAvailable > 0 && !isBuffering;

        if (isAudiblyPlaying) {
          emitPlaybackState('started');
          return;
        }

        emitPlaybackState('ended');
      };

      workletNodeRef.current = workletNode;
      workletReadyRef.current = true;

      // Apply current effect state to the new worklet
      if (currentEffectModeRef.current !== 'off') {
        workletNode.port.postMessage({
          type: 'effect',
          mode: currentEffectModeRef.current,
          intensity: currentEffectIntensityRef.current,
        });
      }

      return workletNode;
    } catch (error) {
      fallbackModeRef.current = true;
      workletReadyRef.current = false;
      workletNodeRef.current = null;
      if (audioContext.state !== 'closed') {
        ensureGainNode(audioContext);
      } else {
        resetAudioGraph();
        audioContextRef.current = null;
      }
      if (!fallbackWarningLoggedRef.current) {
        fallbackWarningLoggedRef.current = true;
        console.warn('[AudioPlayback] AudioWorklet unavailable, using buffer fallback:', error);
      }
      return null;
    }
  }, [emitPlaybackState, ensureGainNode, fadeMs, lowBufferThresholdMs, prebufferMs, resetAudioGraph, sampleRate]);

  const getAudioContext = useCallback(async () => {
    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      audioContextRef.current = new AudioContext({ sampleRate });
      resetAudioGraph();
    }

    const ctx = audioContextRef.current;

    // Resume if suspended (OBS browser sources typically allow autoplay)
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch (err) {
        console.warn('[AudioPlayback] Failed to resume AudioContext:', err);
      }
    }

    // Initialize worklet if needed
    if (!workletReadyRef.current) {
      await initWorklet(ctx);
    }

    return ctx;
  }, [initWorklet, resetAudioGraph, sampleRate]);

  // Unlock audio on any user interaction (for regular browsers)
  const unlockAudio = useCallback(async () => {
    try {
      const ctx = await getAudioContext();
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      audioUnlockedRef.current = ctx.state !== 'closed';
    } catch (error) {
      console.error('[AudioPlayback] Failed to unlock audio:', error);
      throw error;
    }
  }, [getAudioContext]);

  const ensureAudioRunning = useCallback(async () => {
    try {
      const ctx = await getAudioContext();
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
    } catch (error) {
      console.warn('[AudioPlayback] Failed to auto-resume AudioContext:', error);
    }
  }, [getAudioContext]);

  useEffect(() => {
    const tryResume = () => {
      if (!audioUnlockedRef.current) {
        return;
      }
      void ensureAudioRunning();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        tryResume();
      }
    };

    window.addEventListener('focus', tryResume);
    window.addEventListener('pointerdown', tryResume);
    window.addEventListener('keydown', tryResume);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const resumeInterval = window.setInterval(tryResume, 5000);

    return () => {
      window.removeEventListener('focus', tryResume);
      window.removeEventListener('pointerdown', tryResume);
      window.removeEventListener('keydown', tryResume);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.clearInterval(resumeInterval);
    };
  }, [ensureAudioRunning]);

  const playAudio = useCallback(async (audioBuffer: ArrayBuffer) => {
    const audioContext = await getAudioContext();
    if (!audioContext || audioContext.state === 'closed') {
      resetAudioGraph();
      audioContextRef.current = null;
      return;
    }

    try {
      // Convert ArrayBuffer to Float32Array
      const int16Data = new Int16Array(audioBuffer);
      const float32Data = new Float32Array(int16Data.length);
      for (let i = 0; i < int16Data.length; i++) {
        float32Data[i] = int16Data[i] / 32768;
      }

      // If worklet is ready, use it
      const workletNode = await initWorklet(audioContext);
      if (workletNode) {
        workletNode.port.postMessage({
          type: 'audio',
          audioData: float32Data,
        });
        emitPlaybackState('started');
        return;
      }

      // Fallback: use AudioBufferSourceNode
      // Clear any pending fallback timeout before scheduling new audio
      clearFallbackEndedTimeout();

      const gainNode = ensureGainNode(audioContext);
      const buffer = audioContext.createBuffer(1, float32Data.length, sampleRate);
      buffer.getChannelData(0).set(float32Data);

      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(gainNode);
      fallbackSourcesRef.current.add(source);
      source.onended = () => {
        fallbackSourcesRef.current.delete(source);
      };

      const startTime = Math.max(audioContext.currentTime, fallbackNextStartTimeRef.current || audioContext.currentTime);
      const endTime = startTime + buffer.duration;
      fallbackNextStartTimeRef.current = endTime;

      emitPlaybackState('started');
      source.start(startTime);
      scheduleFallbackEndedState(audioContext, endTime);
    } catch (error) {
      if (error instanceof Error && error.message.includes('closed')) {
        resetAudioGraph();
        audioContextRef.current = null;
      }
      console.error('[AudioPlayback] Failed to play:', error);
    }
  }, [clearFallbackEndedTimeout, emitPlaybackState, ensureGainNode, getAudioContext, initWorklet, resetAudioGraph, sampleRate, scheduleFallbackEndedState]);

  const stop = useCallback(() => {
    clearFallbackEndedTimeout();
    stopFallbackSources();
    fallbackNextStartTimeRef.current = 0;
    audioUnlockedRef.current = false;
    emitPlaybackState('cleared');
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (gainNodeRef.current) {
      gainNodeRef.current.disconnect();
      gainNodeRef.current = null;
    }
    workletReadyRef.current = false;
    fallbackModeRef.current = false;
      fallbackWarningLoggedRef.current = false;
      sampleRateMismatchWarningLoggedRef.current = false;

    if (audioContextRef.current) {
      void audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, [clearFallbackEndedTimeout, emitPlaybackState, stopFallbackSources]);

  // Reset playback state without closing context (for session restarts)
  const reset = useCallback(() => {
    if (workletNodeRef.current) {
      workletNodeRef.current.port.postMessage({ type: 'clear' });
      emitPlaybackState('cleared');
      return;
    }

    clearFallbackEndedTimeout();
    stopFallbackSources();
    if (audioContextRef.current) {
      fallbackNextStartTimeRef.current = audioContextRef.current.currentTime;
    }
    emitPlaybackState('cleared');
  }, [clearFallbackEndedTimeout, emitPlaybackState, stopFallbackSources]);

  // Pre-initialize audio context and worklet (call on mount)
  const init = useCallback(async () => {
    try {
      await getAudioContext();
    } catch {
      // Pre-init failed, will retry on first audio
    }
  }, [getAudioContext]);

  // Set output volume (0.0 = mute, 1.0 = normal, 2.0 = 2x boost)
  const setVolume = useCallback((volume: number) => {
    currentVolumeRef.current = volume;
    if (gainNodeRef.current && audioContextRef.current) {
      // Smooth transition to prevent popping
      gainNodeRef.current.gain.setTargetAtTime(
        volume,
        audioContextRef.current.currentTime,
        0.1
      );
    }
    // If GainNode not ready, volume will be applied when audio starts
  }, []);

  // Set audio effect for AI-controlled distortion
  const setAudioEffect = useCallback((mode: AudioEffectMode, intensity: number) => {
    currentEffectModeRef.current = mode;
    currentEffectIntensityRef.current = Math.max(0, Math.min(2, intensity));
    logger.log('[AudioPlayback] Effect changed:', mode, 'intensity:', currentEffectIntensityRef.current);
    if (workletNodeRef.current) {
      workletNodeRef.current.port.postMessage({
        type: 'effect',
        mode,
        intensity: currentEffectIntensityRef.current,
      });
    }
  }, []);

  return useMemo(() => ({
    playAudio,
    stop,
    reset,
    init,
    setVolume,
    setAudioEffect,
    unlockAudio,
  }), [init, playAudio, reset, setAudioEffect, setVolume, stop, unlockAudio]);
}
